import { httpClient } from '../api/http';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert } from '@piggytechng/component-library';
import { authPayload } from '../helpers/utils/auth-payload';

const useCardsAndBanks = () => {
  const params = useParams();
  const userIdIn = params.id as string;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedCardId, setSelectedCardId] = useState<string>('');

  const {
    data: userCards,
    isLoading: isLoadingUserCards,
    refetch: refetchUserCards,
  } = useQuery({
    queryKey: [`user-cards-${userIdIn}`],
    enabled: !!userIdIn,
    queryFn: () => httpClient.get(`admin/user/cards/${userIdIn}`).then((res) => res.data.data.cards),
  });

  // useQuery to fetch banks
  const { data: userBanks, isLoading: isLoadingUserBanks } = useQuery({
    queryKey: [`user-banks-${userIdIn}`],
    enabled: !!userIdIn,
    queryFn: () => httpClient.get(`admin/user/banks/${userIdIn}`).then((res) => res.data.data.banks),
  });

  const deleteCard = async (cardId: string, password: string) => {
    try {
      setIsLoading(true);

      const url = `admin/user/delete-card/${userIdIn}`;
      const payload = {
        cardId,
        ...authPayload(password),
      };

      const response = await httpClient.post<any>(url, payload);

      if (response.data.status) {
        Alert.success(response.data.message || 'Card deleted successfully');
        refetchUserCards();
        return response.data;
      }
    } catch (error: any) {
      console.log(error);
      Alert.error(error?.response?.data?.message || 'Failed to delete card');
    } finally {
      setIsLoading(false);
    }
  };

  // Cached per cardId — re-opening a card the user has already viewed won't refetch.
  const { data: cardUsers, isFetching: isLoadingCardUsers } = useQuery({
    queryKey: [`card-users-${selectedCardId}`],
    enabled: !!selectedCardId,
    queryFn: () => httpClient.get(`admin/user/card-users/${selectedCardId}`).then((res) => res.data?.data?.users ?? []),
  });

  const fetchCardUsers = (cardId: string) => {
    setSelectedCardId(cardId);
  };

  return {
    userCards: userCards || [],
    isLoadingUserCards,
    userBanks: userBanks || [],
    isLoadingUserBanks,
    deleteCard,
    isLoading,
    cardUsers: cardUsers || [],
    isLoadingCardUsers,
    fetchCardUsers,
  };
};

export default useCardsAndBanks;
